import GInput from "../base/GInput";
import GPhoneInput, { isValidPhoneNumber } from "../base/GPhoneInput";
import Modal from "../Modal"
import { useLazyQuery, useMutation } from "@apollo/client";
import { SIGNUP } from "../../../graphql/auth";
import { useEffect, useState } from "react";
import GCheckbox from "../base/GCheckbox";
import GButton from "../base/GButton";
import useMainStore from "../../../store/mainState";
import { VALIDATE_PROMO_CODE } from "../../../graphql/bonuses";
import { useTranslation } from 'react-i18next';
import type { Value } from 'react-phone-number-input';

interface SignUpProps {
    isOpen: boolean;
    onClose: () => void;
    openSignIn: () => void;
    initialPromoCode?: string;
}

const SignUp = ({ isOpen, onClose, openSignIn, initialPromoCode }: SignUpProps) => {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState<Value | undefined>(undefined);
    const [password, setPassword] = useState("");
    const [affiliateId, setAffiliateId] = useState("");
    const [isChecked, setIsChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [promoCode, setPromoCode] = useState(initialPromoCode || "");

    useEffect(() => {
        if (initialPromoCode) {
            setPromoCode(initialPromoCode);
        }
    }, [initialPromoCode, isOpen]);
    const [promoMessage, setPromoMessage] = useState("");
    const [usernameError, setUsernameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [validatePromoCode, { data: promoData, loading: promoLoading, error: promoError }] = useLazyQuery(VALIDATE_PROMO_CODE, {
        fetchPolicy: "network-only",
    });
    const { setIsLoading } = useMainStore();
    const { t } = useTranslation();
    const [signup, { data, loading, error }] = useMutation(SIGNUP, { errorPolicy: 'all' });

    const validateUsername = (value: string) => {
        const alphanumericRegex = /^[a-zA-Z0-9]*$/;
        if (!alphanumericRegex.test(value)) {
            setUsernameError(t('auth.usernameAlphanumeric') || 'Only letters and numbers allowed');
        } else {
            setUsernameError("");
        }
        setUsername(value.replace(/[^a-zA-Z0-9]/g, ''));
    };

    const validateEmail = (value: string) => {
        setEmail(value);
        setUsername(value);
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            setEmailError(t('auth.invalidEmail') || 'Invalid email address');
        } else {
            setEmailError("");
        }
    };

    const validatePhone = (value: string) => {
        const phoneValue = value ? value as Value : undefined;
        setPhone(phoneValue);
        if (value && !isValidPhoneNumber(value)) {
            setPhoneError(t('auth.invalidPhone') || 'Invalid phone number');
        } else {
            setPhoneError("");
        }
    };

    const onSubmit = async () => {
        if (!username || !email || !password || !isChecked) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError(t('auth.invalidEmail') || 'Invalid email address');
            return;
        }

        if (phone && !isValidPhoneNumber(phone)) {
            setPhoneError(t('auth.invalidPhone') || 'Invalid phone number');
            return;
        }

        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            let variables: any = {
                username: username,
                password: password,
                email: email,
            }

            if (phone) {
                variables.phone = phone;
            }

            if (affiliateId) {
                variables.affiliateId = Number(affiliateId);
            }
           
            if (promoCode && promoData?.validatePromoCode?.isValid) {
                variables.promoCode = promoCode;
            }

            const result = await signup({
                variables: variables
            })

            // Check for GraphQL errors in the response
            if (result.errors && result.errors.length > 0) {
                setErrorMessage(result.errors[0].message || t('auth.signUpFailed'));
            } else if (result.data) {
                setSuccessMessage(t('auth.accountCreated'));
                setTimeout(() => {
                    onOpenSignIn();
                }, 2000);
            } else {
                setErrorMessage(t('auth.signUpFailed'));
            }
        } catch (error: any) {
            console.log(error);
            if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                setErrorMessage(error.graphQLErrors[0].message || t('auth.signUpFailed'));
            } else if (error?.networkError) {
                setErrorMessage(t('auth.networkError'));
            } else if (error?.message) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(t('auth.signUpFailedCheck'));
            }
        }
        setIsLoading(false);
    }

    const onOpenSignIn = () => {
        onClose();
        openSignIn();
    }

    const onValidatePromoCode = async () => {
        if (!promoCode || promoCode.trim() === "") {
            setPromoMessage("");
            return;
        }

        try {
            const result = await validatePromoCode({
                variables: {
                    code: promoCode,
                    type: "REGISTRATION",
                },
            });

            if (result.data?.validatePromoCode && result.data.validatePromoCode.isValid) {
                setAffiliateId(result.data.validatePromoCode.promo.ownerId)
            }

            return result.data
        } catch (err: any) {
            console.error("Promo code validation error:", err);
            setPromoMessage(t('auth.promoValidationFailed'));
            return null
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('auth.signUpTitle')} subTitle={t('auth.signUpSubtitle')} leftContent={<img src={require("../../../assets/images/signup.webp")} alt="" className="h-full  object-fill w-full" />}>
            <div>
                {errorMessage && (
                    <div className="mb-5 px-4 py-3 bg-red-500/20 backdrop-blur-sm border border-red-500/40 rounded-[12px] shadow-[0_0_20px_rgba(239,68,68,0.15)] animate-[fadeIn_0.3s_ease-out]">
                        <p className="text-red-400 text-[14px] font-semibold">{errorMessage}</p>
                    </div>
                )}
                {successMessage && (
                    <div className="mb-5 px-4 py-3 bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/40 rounded-[12px] shadow-[0_0_20px_rgba(16,185,129,0.15)] animate-[fadeIn_0.3s_ease-out]">
                        <p className="text-emerald-400 text-[14px] font-semibold">{successMessage}</p>
                    </div>
                )}
                {/* <div>
                    <GInput required className="w-full" label={t('profile.username')} type="text" placeholder={t('profile.username')} value={username} onChange={(e) => validateUsername(e.target.value)} />
                    {usernameError && <p className="text-red-400 text-[12px] -mt-3 mb-3">{usernameError}</p>}
                </div> */}
                <div>
                    <GInput required className="w-full" label={t('auth.email')} type="email" placeholder={t('auth.emailPlaceholder') || 'Enter your email'} value={email} onChange={(e) => validateEmail(e.target.value)} />
                    {emailError && <p className="text-red-400 text-[12px] -mt-3 mb-3">{emailError}</p>}
                </div>
                <GPhoneInput
                    label={t('auth.phone') || 'Phone'}
                    value={phone}
                    onChange={validatePhone}
                    placeholder={t('auth.phonePlaceholder') || 'Enter phone number'}
                    error={phoneError}
                />
                <GInput required className="w-full" label={t('auth.password')} type="password" placeholder={t('auth.password')} onChange={(e) => setPassword(e.target.value)} />
                <GInput className="w-full" label={t('auth.referralCode')} type="text" placeholder={t('auth.referralPlaceholder')} value={promoCode} onChange={(e) => setPromoCode(e.target.value)} validate={onValidatePromoCode} />
                <GCheckbox label={t('auth.agreeTerms')} isChecked={isChecked} setIsChecked={setIsChecked} />
                <GButton disabled={!username || !email || !password || !isChecked || !!emailError || !!phoneError} className="w-full py-[16px] mt-[30px] rounded-[12px]" onClick={onSubmit} isGradient={true}>{t('common.signUp')}</GButton>
                <p className="text-white text-[18px] font-medium mt-[20px] text-center">{t('auth.alreadyHaveAccount')} <span className="text-[#4DBDFF] cursor-pointer hover:text-[#0086D3] transition-colors" onClick={onOpenSignIn}>{t('common.signIn')}</span></p>
            </div>
        </Modal>
    )
}

export default SignUp